import React, { useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";

import TextInput from "../reusable/TextInput";
import Button from "../../../common/ui/Button";

import classes from "./SignUpForm.module.css";
import { useNavigate } from "react-router-dom";

import { useAppStore } from "../../../providers/AppStore";
import { loginUser } from "../../../common/network/Api";
import { UserRole, UserType } from "../../../common/entities/profile";
import useTheme from "../../../common/hooks/useTheme";
import { useAuth } from "../../../providers/AuthProvider";
import { LocalStorageVariables } from "../../../common/entities/type";

export type UserDataType = {
  id?: string;
  role?: number;
  email: string;
  password: string;
  client?: string;
};

type Props = {
  id: string | null;
  setIsLoading: (value: boolean) => void;
  setErrorMessage: (value: string) => void;
  setShowError: (value: boolean) => void;
};

const SignInForm = ({
  id,
  setIsLoading,
  setErrorMessage,
  setShowError,
}: Props) => {
  const { ASGetUserInfo, ASGetUserSettings } = useAppStore();
  // No AuthSetIsSimpleUser: isSimpleUser is derived from AuthUser.role now.
  const { AuthUser, AuthSetUser, AuthBroadcastLogin } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const handleShowPassword = () => {
    setShowPassword(() => !showPassword);
  };

  /**
   * Returns true once navigation away from this screen has been handed off —
   * either a client-side route change or a full page load. The caller must then
   * leave the loader up: clearing it renders the sign-in form again on top of a
   * navigation that has not committed yet. Both hand-offs are deferred, so the
   * form would win the race and flash. `navigate()` route updates run inside
   * React Router's `v7_startTransition` (a low-priority update, while
   * `setIsLoading` is urgent), and `window.location.href` takes as long as a full
   * document load to swap the page.
   */
  const handleLogin = async (userData: UserDataType): Promise<boolean> => {
    try {
      const redirectUrl = localStorage.getItem(
        LocalStorageVariables.REDIRECT_URL
      );

      let temporaryUser: UserType = await loginUser(userData);
      await AuthSetUser(temporaryUser);

      if (temporaryUser.role === UserRole.Customer) {
        const updatedTemporaryUser = await ASGetUserInfo(temporaryUser);
        const usr = await ASGetUserSettings(updatedTemporaryUser);

        // Notify other tabs that a user just signed in. We fire this
        // AFTER the supplementary fetches so localStorage holds the
        // complete user (including questionnaire flags) by the time
        // other tabs reload and `AuthRoute` reads it.
        AuthBroadcastLogin();

        if (redirectUrl) {
          localStorage.removeItem(LocalStorageVariables.REDIRECT_URL);
          window.location.href = redirectUrl;
          return true;
        }

        if (!usr.biometrics_questionnaire_completed) {
          navigate("/intro", { replace: true });
        } else {
          navigate(`/platform/dashboard`, { replace: true });
        }
        return true;
      } else if (
        temporaryUser.role === UserRole.Company ||
        temporaryUser.role === UserRole.CompanyAdmin
      ) {
        const updatedTemporaryUser = await ASGetUserInfo(temporaryUser);
        await AuthSetUser(updatedTemporaryUser);

        // Notify other tabs (see customer branch comment above).
        AuthBroadcastLogin();

        if (redirectUrl) {
          localStorage.removeItem(LocalStorageVariables.REDIRECT_URL);
          window.location.href = redirectUrl;
          return true;
        }
        navigate("/client/client-list", { replace: true });
        return true;
      }

      // Any other role has no destination here — stay on the form.
      return false;
    } catch (e: any) {
      setErrorMessage("Incorrect username or password. Please try again.");
      setShowError(true);
      return false;
    }
  };

  const handleSubmitLogin = async (
    values: UserDataType,
    { setSubmitting }: FormikHelpers<UserDataType>
  ) => {
    setIsLoading(true);
    const user: UserDataType = {
      email: values.email,
      password: values.password,
      client: "WELCOME",
    };

    const isNavigating = await handleLogin(user);
    setSubmitting(false);
    if (!isNavigating) {
      setIsLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        id: "",
        role: 1,
        email: "",
        // email: "dimitriadis90@hotmail.com",
        // email: "theodoros.gilos+02@pnoe.com",
        password: "",
        // password: "Pnoe123456!",
        // password: "Dev_pn03",
      }}
      validationSchema={Yup.object({
        email: Yup.string()
          .email("Invalid email address`")
          .required("This is a required field."),
        password: Yup.string()
          .required("This is a required field.")
          .min(6, "Your password is too short."),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmitLogin}
    >
      <Form className={classes.inner}>
        <TextInput
          label="Email"
          name="email"
          type="email"
          // placeholder="Enter your Email here"
        />
        <TextInput
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          // placeholder="Enter your Password here"
          onClick={handleShowPassword}
          showPwd={showPassword}
        />

        <div
          className={classes.forgetPassword}
          style={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }}
          onClick={() => {
            setErrorMessage("");
            setShowError(false);
            navigate(`/auth?mode=forgot-password&id=${id}`);
          }}
        >
          Forgot Password?
        </div>
        <div className={classes.bottom}>
          <Button
            type="submit"
            text="Sign in"
            customWidth="100%"
            customMargin={"85px auto 0"}
          />
        </div>
      </Form>
    </Formik>
  );
};

export default SignInForm;
